import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, Mail, User, Phone, Calendar, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import Layout from '@/components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventInvitations() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [filteredInvitations, setFilteredInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [event, setEvent] = useState(null);

  useEffect(() => {
    fetchInvitations();
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    filterInvitations();
  }, [searchTerm, filterStatus, invitations]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/events/${eventId}/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data);
      setFilteredInvitations(response.data);
    } catch (error) {
      toast.error('Errore caricamento inviti');
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterInvitations = () => {
    let filtered = invitations;

    // Filter by search term (email, name, phone)
    if (searchTerm) {
      filtered = filtered.filter(inv =>
        inv.invitee_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.invitee_name && inv.invitee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inv.invitee_phone && inv.invitee_phone.includes(searchTerm))
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(inv => inv.status === filterStatus);
    }

    setFilteredInvitations(filtered);
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: { color: 'bg-blue-100 text-blue-700', label: 'Inviato' },
      opened: { color: 'bg-yellow-100 text-yellow-700', label: 'Aperto' },
      registered: { color: 'bg-green-100 text-green-700', label: 'Registrato' },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancellato' }
    };
    
    const badge = badges[status] || badges.sent;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/events/${eventId}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna all'evento
          </Button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Inviti Evento
          </h1>
          {event && (
            <p className="text-gray-600">{event.title}</p>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Totale Inviti</div>
              <div className="text-2xl font-bold">{invitations.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Inviati</div>
              <div className="text-2xl font-bold text-blue-600">
                {invitations.filter(i => i.status === 'sent').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Aperti</div>
              <div className="text-2xl font-bold text-yellow-600">
                {invitations.filter(i => i.status === 'opened').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Registrati</div>
              <div className="text-2xl font-bold text-green-600">
                {invitations.filter(i => i.status === 'registered').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Cerca per email, nome o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Status Filter */}
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  Tutti
                </Button>
                <Button
                  variant={filterStatus === 'sent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('sent')}
                >
                  Inviati
                </Button>
                <Button
                  variant={filterStatus === 'opened' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('opened')}
                >
                  Aperti
                </Button>
                <Button
                  variant={filterStatus === 'registered' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('registered')}
                >
                  Registrati
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invitations Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Invitati ({filteredInvitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredInvitations.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Nessun invito trovato con i filtri selezionati'
                    : 'Nessun invito inviato ancora'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Nome
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Telefono
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Inviato il
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredInvitations.map((invitation) => (
                      <tr key={invitation.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {invitation.invitee_email}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">
                              {invitation.invitee_name || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">
                              {invitation.invitee_phone || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getStatusBadge(invitation.status)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">
                              {format(new Date(invitation.sent_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
